import type { TFunction } from 'i18next'
import type { LexProduct } from '@/shared/hooks/api/product'

export type LlmChatSessionScopeKind = 'general' | 'product' | 'deleted_product'

export type LlmChatSessionScopeDisplay = {
  scopeKind: LlmChatSessionScopeKind
  label: string
}

export function buildProductDisplayNameMap(
  products: ReadonlyArray<LexProduct>,
  noNameFallback: string
): Map<string, string> {
  const map = new Map<string, string>()
  for (const product of products) {
    const name = product.name?.trim()
    map.set(
      product.id,
      name !== undefined && name.length > 0 ? name : noNameFallback
    )
  }
  return map
}

export function resolveLlmChatSessionScopeKind(input: {
  productId: string | null | undefined
  productDisplayNameMap: ReadonlyMap<string, string>
  productsLoaded: boolean
}): LlmChatSessionScopeKind | null {
  const productId = input.productId?.trim()
  if (productId === undefined || productId === null || productId.length === 0) {
    return 'general'
  }
  if (!input.productsLoaded) {
    return null
  }
  if (input.productDisplayNameMap.has(productId)) {
    return 'product'
  }
  return 'deleted_product'
}

export function formatLlmChatSessionScopeLabel(input: {
  scopeKind: LlmChatSessionScopeKind
  translate: TFunction<'llmChat'>
  productDisplayNameMap: ReadonlyMap<string, string>
  productId: string | null | undefined
}): string {
  if (input.scopeKind === 'general') {
    return input.translate('sessionScope.general')
  }
  if (input.scopeKind === 'deleted_product') {
    return input.translate('sessionScope.deletedProduct')
  }
  const productId = input.productId?.trim() ?? ''
  const productName =
    input.productDisplayNameMap.get(productId) ??
    input.translate('newChatWizard.productSelect.noName')
  return input.translate('sessionScope.product', { productName })
}

export function getLlmChatSessionScopeDisplay(input: {
  productId: string | null | undefined
  productDisplayNameMap: ReadonlyMap<string, string>
  productsLoaded: boolean
  translate: TFunction<'llmChat'>
}): LlmChatSessionScopeDisplay | null {
  const scopeKind = resolveLlmChatSessionScopeKind({
    productId: input.productId,
    productDisplayNameMap: input.productDisplayNameMap,
    productsLoaded: input.productsLoaded,
  })
  if (scopeKind === null) {
    return null
  }
  return {
    scopeKind,
    label: formatLlmChatSessionScopeLabel({
      scopeKind,
      translate: input.translate,
      productDisplayNameMap: input.productDisplayNameMap,
      productId: input.productId,
    }),
  }
}
